import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiEye, FiCalendar, FiDollarSign, FiUsers, FiLightbulb } = FiIcons;

const Insights = () => {
  const [timeRange, setTimeRange] = useState('30d');

  // Sample data for charts
  const viewsData = {
    title: { text: 'Views Over Time' },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    },
    yAxis: { type: 'value' },
    series: [{
      data: [45, 52, 38, 61, 48, 73, 56],
      type: 'line',
      smooth: true,
      itemStyle: { color: '#0ea5e9' }
    }]
  };

  const bookingsData = {
    title: { text: 'Bookings by Service' },
    tooltip: { trigger: 'item' },
    series: [{
      type: 'pie',
      radius: '60%',
      data: [
        { value: 18, name: 'Home Cleaning' },
        { value: 15, name: 'Garden Maintenance' },
        { value: 12, name: 'Pet Sitting' },
        { value: 8, name: 'Tutoring' }
      ],
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }]
  };

  const revenueData = {
    title: { text: 'Revenue Trends' },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']
    },
    yAxis: { type: 'value' },
    series: [{
      data: [2100, 2400, 1800, 2800, 2600, 3240],
      type: 'bar',
      itemStyle: { color: '#8fa05e' }
    }]
  };

  const leadSourceData = {
    title: { text: 'Source of Leads' },
    tooltip: { trigger: 'item' },
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      data: [
        { value: 35, name: 'Google Search' },
        { value: 28, name: 'WhatsApp' },
        { value: 20, name: 'Referrals' },
        { value: 12, name: 'Flyers' },
        { value: 5, name: 'Social Media' }
      ]
    }]
  };

  const aiTips = [
    {
      icon: FiCalendar,
      title: 'Weekend Availability',
      message: 'Clients booking your tutoring service often ask about weekend availability. Want to add that?',
      priority: 'High'
    },
    {
      icon: FiDollarSign,
      title: 'Pricing Optimization',
      message: 'Your cleaning service has 15% higher demand than average. Consider a small price increase.',
      priority: 'Medium'
    },
    {
      icon: FiUsers,
      title: 'Client Retention',
      message: 'Clients who book multiple services have 40% higher lifetime value. Consider service bundles.',
      priority: 'Medium'
    },
    {
      icon: FiTrendingUp,
      title: 'Peak Hours',
      message: 'Most bookings happen between 2-4 PM. Consider promotional pricing for off-peak hours.',
      priority: 'Low'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <motion.div 
        className="flex items-center justify-between"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Insights</h1>
          <p className="text-slate-600 mt-1">Understand how your business is doing</p>
        </div>
        
        <div className="flex items-center gap-2">
          {['7d', '30d', '90d', '1y'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                timeRange === range
                  ? 'bg-trust-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Total Views', value: '1,234', change: '+12%', icon: FiEye },
          { label: 'Total Bookings', value: '53', change: '+8%', icon: FiCalendar },
          { label: 'Total Revenue', value: '$4,280', change: '+15%', icon: FiDollarSign },
          { label: 'New Clients', value: '18', change: '+22%', icon: FiUsers },
        ].map((metric, index) => (
          <motion.div
            key={metric.label}
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-trust-100">
                <SafeIcon icon={metric.icon} className="text-xl text-trust-600" />
              </div>
              <span className="text-green-600 text-sm font-medium">{metric.change}</span>
            </div>
            <div className="text-2xl font-bold text-slate-800 mb-1">{metric.value}</div>
            <div className="text-slate-600 text-sm">{metric.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Views Chart */}
        <motion.div 
          className="bg-white rounded-xl p-6 border border-slate-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <ReactECharts option={viewsData} style={{ height: '300px' }} />
        </motion.div>

        {/* Bookings Chart */}
        <motion.div 
          className="bg-white rounded-xl p-6 border border-slate-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <ReactECharts option={bookingsData} style={{ height: '300px' }} />
        </motion.div>

        {/* Revenue Chart */}
        <motion.div 
          className="bg-white rounded-xl p-6 border border-slate-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <ReactECharts option={revenueData} style={{ height: '300px' }} />
        </motion.div>

        {/* Lead Source Chart */}
        <motion.div 
          className="bg-white rounded-xl p-6 border border-slate-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <ReactECharts option={leadSourceData} style={{ height: '300px' }} />
        </motion.div>
      </div>

      {/* AI Tips */}
      <motion.div 
        className="bg-white rounded-xl p-6 border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9 }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg">
            <SafeIcon icon={FiLightbulb} className="text-white text-xl" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">AI Business Tips</h2>
            <p className="text-slate-600">Smart recommendations based on your data</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {aiTips.map((tip, index) => (
            <motion.div
              key={index}
              className="p-4 bg-gradient-to-r from-slate-50 to-trust-50 rounded-lg border border-slate-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-white rounded-lg">
                  <SafeIcon icon={tip.icon} className="text-trust-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-800">{tip.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      tip.priority === 'High' ? 'bg-red-100 text-red-800' :
                      tip.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {tip.priority}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm">{tip.message}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Insights;